import { useState, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { TrendingUp, TrendingDown, CreditCard, DollarSign, ShoppingCart, Calendar, Plus, Trash2, RefreshCw, Eye, Settings, BarChart3, Users, Clock, Lock, ChevronLeft, ChevronRight, Mail, UserCheck, Search, Send, Save, FileText, Check, X, Edit2, MessageSquare } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// DOMPurify safe HTML sanitizer for email preview (prevents XSS)
const sanitizeEmailHtml = (dirty) =>
  DOMPurify.sanitize(dirty || '', {
    ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'u', 'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'div', 'span', 'blockquote', 'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'pre', 'code', 'small'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'src', 'alt', 'width', 'height', 'align'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/(?:png|jpe?g|gif|webp)):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });

// Utility functions
const generateTransactionId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let id = 'tr_';
  for (let i = 0; i < 10; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
};

const generateCardLast4 = () => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

const getCardBrand = () => {
  const brands = ['visa', 'mastercard', 'applepay', 'paypal'];
  // Weighted distribution pour plus de réalisme
  const weights = [35, 35, 15, 15]; // Visa et Mastercard plus fréquents
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  let random = Math.random() * totalWeight;
  for (let i = 0; i < brands.length; i++) {
    random -= weights[i];
    if (random <= 0) return brands[i];
  }
  return brands[0];
};

// Payment method logos as SVG components
const PaymentLogos = {
  visa: () => (
    <svg viewBox="0 0 48 32" className="h-6 w-auto">
      <rect width="48" height="32" rx="4" fill="#1A1F71"/>
      <path d="M19.5 21h-2.7l1.7-10.5h2.7L19.5 21zm-5-10.5l-2.6 7.2-.3-1.5-.9-4.6s-.1-.6-.8-.6h-4.4l-.1.3s1.1.2 2.4.9l2 7.8h2.8l4.3-10.5h-2.4zm21.2 10.5h2.4l-2.1-10.5h-2.2c-.5 0-1 .3-1.2.8l-4 9.7h2.8l.6-1.6h3.4l.3 1.6zm-3-3.8l1.4-3.9.8 3.9h-2.2zm-4.9-3.7l.4-2.2s-1.2-.5-2.5-.5c-1.4 0-4.6.6-4.6 3.5 0 2.7 3.8 2.8 3.8 4.2 0 1.4-3.4 1.2-4.5.3l-.4 2.3s1.2.6 3.1.6c1.9 0 4.8-1 4.8-3.6 0-2.7-3.8-3-3.8-4.2 0-1.2 2.6-1 3.7-.4z" fill="#fff"/>
    </svg>
  ),
  mastercard: () => (
    <svg viewBox="0 0 48 32" className="h-6 w-auto">
      <rect width="48" height="32" rx="4" fill="#000"/>
      <circle cx="18" cy="16" r="8" fill="#EB001B"/>
      <circle cx="30" cy="16" r="8" fill="#F79E1B"/>
      <path d="M24 9.8a8 8 0 000 12.4 8 8 0 000-12.4z" fill="#FF5F00"/>
    </svg>
  ),
  applepay: () => (
    <svg viewBox="0 0 48 32" className="h-6 w-auto">
      <rect width="48" height="32" rx="4" fill="#000"/>
      <path d="M12.3 11.2c-.4.5-1 .9-1.6.8-.1-.6.2-1.3.5-1.7.4-.5 1-.8 1.5-.8.1.6-.2 1.2-.4 1.7zm.5.9c-.9 0-1.7.5-2.1.5-.4 0-1.1-.5-1.8-.5-1 0-1.8.6-2.3 1.4-1 1.7-.3 4.3.7 5.7.5.7 1 1.5 1.8 1.4.7 0 1-.5 1.8-.5s1.1.5 1.8.5 1.3-.7 1.8-1.4c.5-.8.8-1.6.8-1.6s-1.5-.6-1.5-2.4c0-1.5 1.2-2.2 1.3-2.2-.7-1-1.7-1.1-2.1-1.1z" fill="#fff"/>
      <text x="20" y="20" fill="#fff" fontSize="9" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="600">Pay</text>
    </svg>
  ),
  paypal: () => (
    <svg viewBox="0 0 48 32" className="h-6 w-auto">
      <rect width="48" height="32" rx="4" fill="#003087"/>
      <path d="M18.5 8h5.2c2.8 0 4.8 1.8 4.4 4.5-.4 3.2-2.8 5-5.8 5h-1.5c-.4 0-.8.3-.9.8l-.8 5.2c0 .2-.2.5-.5.5h-3c-.3 0-.5-.3-.4-.6l2.4-14.8c.1-.4.5-.6.9-.6z" fill="#fff"/>
      <path d="M32.5 8h5.2c2.8 0 4.8 1.8 4.4 4.5-.4 3.2-2.8 5-5.8 5h-1.5c-.4 0-.8.3-.9.8l-.8 5.2c0 .2-.2.5-.5.5h-3c-.3 0-.5-.3-.4-.6l2.4-14.8c.1-.4.5-.6.9-.6z" fill="#0070E0"/>
    </svg>
  )
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (date) => {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date));
};

const formatShortDate = (date) => {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' }).format(new Date(date));
};

// Generate realistic transactions with NATURAL random distribution over a period
const generateTransactions = (count, price, days, maxPerDay = 3) => {
  const transactions = [];
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  
  // Create a natural distribution: some days 0, some days 1, some days 2-3
  const dateSlots = [];
  let remaining = count;
  
  // First, randomly assign transactions to days across the ENTIRE period
  while (remaining > 0) {
    // Pick a random day within the period
    const dayOffset = Math.floor(Math.random() * days);
    
    // Random number of transactions for this day (1 to maxPerDay)
    // But weighted towards fewer transactions for realism
    const weights = [50, 30, 15, 5]; // 50% chance of 1, 30% chance of 2, etc.
    let transactionsToAdd = 1;
    const rand = Math.random() * 100;
    let cumulative = 0;
    for (let i = 0; i < Math.min(maxPerDay, weights.length); i++) {
      cumulative += weights[i];
      if (rand < cumulative) {
        transactionsToAdd = i + 1;
        break;
      }
    }
    transactionsToAdd = Math.min(transactionsToAdd, remaining);
    
    // Add transactions for this day
    for (let t = 0; t < transactionsToAdd; t++) {
      const date = new Date(now);
      date.setDate(date.getDate() - dayOffset);
      date.setHours(Math.floor(Math.random() * 14) + 8); // 8h-22h
      date.setMinutes(Math.floor(Math.random() * 60));
      dateSlots.push(date);
    }
    remaining -= transactionsToAdd;
  }
  
  // Sort by date descending
  dateSlots.sort((a, b) => b - a);
  
  for (let i = 0; i < count; i++) {
    transactions.push({
      id: generateTransactionId(),
      date: dateSlots[i].toISOString(),
      amount: price,
      cardBrand: getCardBrand(),
      cardLast4: generateCardLast4(),
      status: 'paid',
      name: generateMaskedName()
    });
  }
  
  return transactions;
};

const generateMaskedName = () => {
  const firstNames = ['M', 'J', 'P', 'S', 'L', 'A', 'C', 'N', 'T', 'E'];
  const lastNames = ['D', 'M', 'L', 'B', 'R', 'G', 'P', 'T', 'V', 'F'];
  const first = firstNames[Math.floor(Math.random() * firstNames.length)];
  const last = lastNames[Math.floor(Math.random() * lastNames.length)];
  return `${first}*** ${last}***`;
};

export function AdminDashboard() {
  const [auth, setAuth] = useState(false);
  const [pwd, setPwd] = useState('');
  const [tab, setTab] = useState('dashboard');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  
  // Settings protection
  const [settingsUnlocked, setSettingsUnlocked] = useState(false);
  const [settingsCode, setSettingsCode] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const SETTINGS_CODE = 'Mayaetlili1';
  
  // Pagination for transactions
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 15;
  
  // Dashboard state
  const [period, setPeriod] = useState('30');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [salesBlocks, setSalesBlocks] = useState([]);
  
  // Settings form
  const [newBlock, setNewBlock] = useState({ count: 10, price: 197, days: 30, maxPerDay: 3 });

  // Load data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('hda_sales_data');
    if (saved) {
      const data = JSON.parse(saved);
      setTransactions(data.transactions || []);
      setSalesBlocks(data.blocks || []);
    }
  }, []);

  // Save data to localStorage
  const saveData = (newTransactions, newBlocks) => {
    localStorage.setItem('hda_sales_data', JSON.stringify({
      transactions: newTransactions,
      blocks: newBlocks
    }));
  };

  function doLogin() {
    if (pwd === 'hda2024') setAuth(true);
  }

  useEffect(() => {
    if (!auth) return;
    if (tab === 'rdv' || tab === 'stats' || tab === 'dispo') {
      setBusy(true);
      const endpoint = tab === 'rdv' ? '/api/bookings' : tab === 'stats' ? '/api/analytics/stats' : '/api/availability/config';
      fetch(API + endpoint).then(r => r.json()).then(d => {
        if (tab === 'rdv') setBookings(d.bookings || []);
        else if (tab === 'stats') setStats(d);
        else setCfg(d);
        setBusy(false);
      }).catch(() => setBusy(false));
    }
  }, [auth, tab]);

  const saveCfg = () => {
    fetch(API + '/api/availability/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg) })
      .then(() => alert('Sauvegardé !'));
  };

  const toggleD = (d) => {
    const arr = cfg?.days_available || [];
    setCfg({ ...cfg, days_available: arr.includes(d) ? arr.filter(x => x !== d) : [...arr, d] });
  };

  // Dashboard calculations
  const getFilteredTransactions = () => {
    const now = new Date();
    
    // If custom dates are set
    if (period === 'custom' && customDateStart && customDateEnd) {
      const start = new Date(customDateStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(customDateEnd);
      end.setHours(23, 59, 59, 999);
      return transactions.filter(t => {
        const d = new Date(t.date);
        return d >= start && d <= end;
      });
    }
    
    const daysAgo = parseInt(period);
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - daysAgo);
    return transactions.filter(t => new Date(t.date) >= cutoff);
  };

  const getDashboardStats = () => {
    const filtered = getFilteredTransactions();
    const total = filtered.reduce((sum, t) => sum + t.amount, 0);
    const count = filtered.length;
    const avg = count > 0 ? total / count : 0;
    
    return { total, count, avg };
  };

  const getChartData = () => {
    const filtered = getFilteredTransactions();
    const data = [];
    
    let startDate, endDate;
    
    if (period === 'custom' && customDateStart && customDateEnd) {
      startDate = new Date(customDateStart);
      endDate = new Date(customDateEnd);
    } else {
      const daysAgo = parseInt(period);
      endDate = new Date();
      startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo + 1);
    }
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Generate data for each day in the range
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const nextDate = new Date(currentDate);
      nextDate.setDate(nextDate.getDate() + 1);
      
      const dayTotal = filtered
        .filter(t => {
          const d = new Date(t.date);
          return d >= currentDate && d < nextDate;
        })
        .reduce((sum, t) => sum + t.amount, 0);
      
      data.push({ 
        date: formatShortDate(currentDate), 
        amount: dayTotal 
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return data;
  };

  // Settings functions
  const addSalesBlock = () => {
    const newTransactions = generateTransactions(newBlock.count, newBlock.price, newBlock.days, newBlock.maxPerDay);
    const block = {
      id: Date.now().toString(),
      ...newBlock,
      transactionIds: newTransactions.map(t => t.id),
      createdAt: new Date().toISOString()
    };
    
    const updatedTransactions = [...transactions, ...newTransactions].sort((a, b) => new Date(b.date) - new Date(a.date));
    const updatedBlocks = [...salesBlocks, block];
    
    setTransactions(updatedTransactions);
    setSalesBlocks(updatedBlocks);
    saveData(updatedTransactions, updatedBlocks);
    setNewBlock({ count: 10, price: 197, days: 30, maxPerDay: 3 });
  };

  const deleteBlock = (blockId) => {
    const block = salesBlocks.find(b => b.id === blockId);
    if (!block) return;
    
    const updatedTransactions = transactions.filter(t => !block.transactionIds.includes(t.id));
    const updatedBlocks = salesBlocks.filter(b => b.id !== blockId);
    
    setTransactions(updatedTransactions);
    setSalesBlocks(updatedBlocks);
    saveData(updatedTransactions, updatedBlocks);
  };

  const resetAllData = () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les données ?')) {
      setTransactions([]);
      setSalesBlocks([]);
      localStorage.removeItem('hda_sales_data');
    }
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#111] p-8 rounded-2xl w-full max-w-sm border border-[#222] shadow-2xl">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">Admin HDA</h1>
          <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} onKeyDown={e => e.key === 'Enter' && doLogin()} placeholder="Mot de passe" className="w-full bg-[#1a1a1a] border border-[#333] rounded-xl px-4 py-3 text-white mb-4 focus:border-[#00FF94] focus:outline-none transition-colors" />
          <button onClick={doLogin} className="w-full bg-[#00FF94] text-black font-bold py-3 rounded-xl hover:bg-[#00FF94]/90 transition-colors">Connexion</button>
        </div>
      </div>
    );
  }

  const dashboardStats = getDashboardStats();
  const chartData = getChartData();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header - Mobile optimized */}
      <header className="bg-[#111] border-b border-[#1a1a1a] px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
        <div className="flex items-center gap-2 md:gap-3">
          <h1 className="text-lg md:text-xl font-bold">Dashboard</h1>
          <span className="text-[#00FF94] text-xs md:text-sm font-medium hidden sm:inline">@samba_bigsam</span>
        </div>
        <button onClick={() => setAuth(false)} className="text-[#666] hover:text-white transition-colors text-xs md:text-sm">Déconnexion</button>
      </header>

      {/* Navigation - Scrollable on mobile */}
      <nav className="bg-[#111] border-b border-[#1a1a1a] px-2 md:px-6 overflow-x-auto scrollbar-hide">
        <div className="flex gap-0 md:gap-1 min-w-max">
          {[
            { id: 'dashboard', label: 'Dashboard', shortLabel: 'Home', icon: BarChart3 },
            { id: 'rdv', label: 'Rendez-vous', shortLabel: 'RDV', icon: Calendar },
            { id: 'prospects', label: 'Prospects', shortLabel: 'CRM', icon: UserCheck },
            { id: 'emails', label: 'E-mails', shortLabel: 'Mails', icon: Mail },
            { id: 'stats', label: 'Analytics', shortLabel: 'Stats', icon: TrendingUp },
            { id: 'dispo', label: 'Disponibilités', shortLabel: 'Dispo', icon: Clock },
            { id: 'settings', label: 'Paramètres', shortLabel: 'Config', icon: Settings },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-3 text-xs md:text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                tab === t.id ? 'border-[#00FF94] text-[#00FF94]' : 'border-transparent text-[#666] hover:text-white'
              }`}
            >
              <t.icon size={14} className="md:w-4 md:h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.shortLabel}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <main className="p-3 md:p-6 max-w-7xl mx-auto">
        {/* DASHBOARD TAB */}
        {tab === 'dashboard' && (
          <div className="space-y-6 animate-fadeIn">
            {/* Period Selector - Mobile optimized */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h2 className="text-base md:text-lg font-semibold">Vue d'ensemble</h2>
                <select
                  value={period}
                  onChange={(e) => {
                    setPeriod(e.target.value);
                    setCurrentPage(1); // Reset pagination when period changes
                    if (e.target.value !== 'custom') {
                      setShowCustomDates(false);
                    } else {
                      setShowCustomDates(true);
                    }
                  }}
                  className="w-full sm:w-auto bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                >
                  <option value="1">Aujourd'hui</option>
                  <option value="7">7 jours</option>
                  <option value="30">30 jours</option>
                  <option value="90">90 jours</option>
                  <option value="365">Année</option>
                  <option value="custom">Personnalisé</option>
                </select>
              </div>
              
              {(period === 'custom' || showCustomDates) && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input
                    type="date"
                    value={customDateStart}
                    onChange={(e) => {
                      setCustomDateStart(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                  />
                  <span className="text-[#666] text-center">→</span>
                  <input
                    type="date"
                    value={customDateEnd}
                    onChange={(e) => {
                      setCustomDateEnd(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                  />
                </div>
              )}
            </div>

            {/* CA Principal - Simple Style */}
            <div className="bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-[#1a1a1a] rounded-xl md:rounded-2xl p-5 md:p-8 text-center">
              <p className="text-xs md:text-sm text-[#888] mb-1 md:mb-2">Chiffre d'affaires</p>
              <p className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#00FF94] tracking-tight">
                {formatCurrency(dashboardStats.total)}
              </p>
              <div className="flex items-center justify-center gap-4 md:gap-8 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-[#1a1a1a]">
                <div className="text-center">
                  <p className="text-lg md:text-2xl font-bold text-white">{dashboardStats.count}</p>
                  <p className="text-[10px] md:text-xs text-[#666]">ventes</p>
                </div>
                <div className="w-px h-8 bg-[#333]"></div>
                <div className="text-center">
                  <p className="text-lg md:text-2xl font-bold text-white">{formatCurrency(dashboardStats.avg)}</p>
                  <p className="text-[10px] md:text-xs text-[#666]">panier moyen</p>
                </div>
              </div>
            </div>

            {/* Chart - Mobile optimized */}
            <div className="bg-[#111] border border-[#1a1a1a] rounded-xl md:rounded-2xl p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs md:text-sm font-medium text-[#888]">Ventes</h3>
                <div className="flex items-center gap-2 text-xs text-[#666]">
                  <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#00FF94]"></div>
                  <span className="hidden sm:inline">Revenus</span>
                </div>
              </div>
              
              {/* Recharts Interactive Chart */}
              <div className="h-48 md:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00FF94" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#00FF94" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#666', fontSize: 9 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#666', fontSize: 9 }}
                      tickFormatter={(value) => `${value}€`}
                      width={45}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a1a', 
                        border: '1px solid #333',
                        borderRadius: '8px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                        fontSize: '12px'
                      }}
                      labelStyle={{ color: '#888', marginBottom: '4px' }}
                      formatter={(value) => [formatCurrency(value), 'Revenus']}
                      cursor={{ stroke: '#00FF94', strokeWidth: 1, strokeDasharray: '5 5' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#00FF94"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                      animationDuration={1500}
                      animationEasing="ease-out"
                      dot={false}
                      activeDot={{ r: 6, stroke: '#00FF94', strokeWidth: 2, fill: '#00FF94' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Transactions - Mobile cards / Desktop table with Pagination */}
            <div className="bg-[#111] border border-[#1a1a1a] rounded-xl md:rounded-2xl overflow-hidden">
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-[#1a1a1a] flex items-center justify-between">
                <h3 className="text-sm md:text-base font-medium">Dernières transactions</h3>
                <span className="text-xs md:text-sm text-[#888]">
                  {getFilteredTransactions().length} transaction{getFilteredTransactions().length !== 1 ? 's' : ''}
                </span>
              </div>
              
              {/* Mobile: Cards view with pagination */}
              <div className="md:hidden divide-y divide-[#1a1a1a]">
                {getFilteredTransactions()
                  .slice((currentPage - 1) * transactionsPerPage, currentPage * transactionsPerPage)
                  .map((t) => {
                  const Logo = PaymentLogos[t.cardBrand] || PaymentLogos.visa;
                  return (
                    <div key={t.id} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-white">{formatCurrency(t.amount)}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#00FF94]/10 text-[#00FF94]">
                          Payé
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#888]">
                        <div className="flex items-center gap-2">
                          <Logo />
                          <span>•••• {t.cardLast4}</span>
                        </div>
                        <span>{formatDate(t.date)}</span>
                      </div>
                    </div>
                  );
                })}
                {getFilteredTransactions().length === 0 && (
                  <div className="text-center py-8 text-[#666] text-sm">
                    Aucune transaction
                  </div>
                )}
              </div>
              
              {/* Desktop: Table view with pagination */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                      <th className="px-6 py-3 font-medium">Date</th>
                      <th className="px-6 py-3 font-medium">Montant</th>
                      <th className="px-6 py-3 font-medium">Paiement</th>
                      <th className="px-6 py-3 font-medium">Carte</th>
                      <th className="px-6 py-3 font-medium">Statut</th>
                      <th className="px-6 py-3 font-medium">ID Transaction</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1a1a1a]">
                    {getFilteredTransactions()
                      .slice((currentPage - 1) * transactionsPerPage, currentPage * transactionsPerPage)
                      .map((t) => {
                      const Logo = PaymentLogos[t.cardBrand] || PaymentLogos.visa;
                      const maskedId = t.id.substring(0, 6) + '••••••';
                      return (
                        <tr key={t.id} className="hover:bg-[#1a1a1a]/50 transition-colors">
                          <td className="px-6 py-4 text-sm">{formatDate(t.date)}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-white">{formatCurrency(t.amount)}</td>
                          <td className="px-6 py-4">
                            <Logo />
                          </td>
                          <td className="px-6 py-4 text-sm text-[#888] font-mono">•••• {t.cardLast4}</td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/20">
                              Payé
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="flex items-center gap-2 text-sm text-[#555] font-mono">
                              <Lock size={12} className="text-[#444]" />
                              {maskedId}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {getFilteredTransactions().length === 0 && (
                  <div className="text-center py-12 text-[#666]">
                    Aucune transaction pour cette période
                  </div>
                )}
              </div>
              
              {/* Pagination Controls */}
              {getFilteredTransactions().length > transactionsPerPage && (
                <div className="px-4 md:px-6 py-3 md:py-4 border-t border-[#1a1a1a] flex items-center justify-between">
                  <div className="text-xs md:text-sm text-[#888]">
                    Page {currentPage} sur {Math.ceil(getFilteredTransactions().length / transactionsPerPage)}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        currentPage === 1 
                          ? 'text-[#444] cursor-not-allowed' 
                          : 'text-white hover:bg-[#1a1a1a]'
                      }`}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, Math.ceil(getFilteredTransactions().length / transactionsPerPage)) }, (_, i) => {
                        const totalPages = Math.ceil(getFilteredTransactions().length / transactionsPerPage);
                        let pageNum;
                        
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-[#00FF94] text-black'
                                : 'text-[#888] hover:bg-[#1a1a1a] hover:text-white'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(getFilteredTransactions().length / transactionsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(getFilteredTransactions().length / transactionsPerPage)}
                      className={`p-2 rounded-lg transition-colors ${
                        currentPage >= Math.ceil(getFilteredTransactions().length / transactionsPerPage)
                          ? 'text-[#444] cursor-not-allowed' 
                          : 'text-white hover:bg-[#1a1a1a]'
                      }`}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SETTINGS TAB - Protected with code */}
        {tab === 'settings' && (
          <div className="space-y-4 md:space-y-6 animate-fadeIn">
            {!settingsUnlocked ? (
              /* Code protection screen */
              <div className="max-w-md mx-auto mt-8">
                <div className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-6 md:p-8 text-center">
                  <div className="inline-flex items-center justify-center bg-[#00FF94]/20 rounded-full p-4 mb-4">
                    <Lock size={32} className="text-[#00FF94]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Zone protégée</h3>
                  <p className="text-[#888] text-sm mb-6">Entre le code d'accès pour modifier les paramètres</p>
                  
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    if (settingsCode === SETTINGS_CODE) {
                      setSettingsUnlocked(true);
                      setSettingsError('');
                    } else {
                      setSettingsError('Code incorrect');
                    }
                  }}>
                    <input
                      type="password"
                      value={settingsCode}
                      onChange={(e) => setSettingsCode(e.target.value)}
                      placeholder="Code d'accès"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-xl px-4 py-3 text-white text-center focus:border-[#00FF94] focus:outline-none transition-colors mb-3"
                      autoFocus
                    />
                    {settingsError && (
                      <p className="text-red-400 text-sm mb-3">{settingsError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full bg-[#00FF94] text-black font-bold py-3 rounded-xl hover:bg-[#00FF94]/90 transition-colors"
                    >
                      Déverrouiller
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              /* Settings content - only shown when unlocked */
              <>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <h2 className="text-base md:text-lg font-semibold">Générateur de ventes</h2>
                  <button
                    onClick={resetAllData}
                    className="flex items-center gap-2 px-3 py-2 text-xs md:text-sm text-red-400 hover:text-red-300 transition-colors bg-red-400/10 rounded-lg"
                  >
                    <RefreshCw size={14} />
                    Réinitialiser
                  </button>
                </div>

                {/* Case study video URL (used by the setter workspace) */}
                <CaseStudyVideoConfig />

                {/* New Block Form - Mobile optimized */}
                <div className="bg-[#111] border border-[#1a1a1a] rounded-xl md:rounded-2xl p-4 md:p-6">
                  <h3 className="text-sm md:text-base font-medium mb-4">Ajouter des ventes</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    <div>
                      <label className="block text-xs md:text-sm text-[#888] mb-1.5 md:mb-2">Nb ventes</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={newBlock.count}
                        onChange={(e) => setNewBlock({ ...newBlock, count: parseInt(e.target.value) || 0 })}
                        className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg md:rounded-xl px-3 py-2.5 md:py-3 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm text-[#888] mb-1.5 md:mb-2">Prix (€)</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={newBlock.price}
                        onChange={(e) => setNewBlock({ ...newBlock, price: parseInt(e.target.value) || 0 })}
                        className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg md:rounded-xl px-3 py-2.5 md:py-3 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm text-[#888] mb-1.5 md:mb-2">Période</label>
                      <select
                        value={newBlock.days}
                        onChange={(e) => setNewBlock({ ...newBlock, days: parseInt(e.target.value) })}
                        className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg md:rounded-xl px-3 py-2.5 md:py-3 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                      >
                        <option value="7">7 jours</option>
                        <option value="14">14 jours</option>
                        <option value="30">30 jours</option>
                        <option value="60">60 jours</option>
                        <option value="90">90 jours</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm text-[#888] mb-1.5 md:mb-2">Max/jour</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={newBlock.maxPerDay}
                        onChange={(e) => setNewBlock({ ...newBlock, maxPerDay: parseInt(e.target.value) || 1 })}
                        className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg md:rounded-xl px-3 py-2.5 md:py-3 text-sm focus:border-[#00FF94] focus:outline-none transition-colors"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                  
                  {/* Preview - Mobile optimized */}
                  <div className="mt-4 p-3 md:p-4 bg-[#0a0a0a] rounded-lg md:rounded-xl border border-[#222]">
                    <div className="flex items-center gap-2 text-xs text-[#888] mb-2">
                      <Eye size={12} />
                      Aperçu
                    </div>
                    <p className="text-sm md:text-lg">
                      <span className="text-[#00FF94] font-semibold">{newBlock.count}</span> × <span className="text-[#00FF94] font-semibold">{formatCurrency(newBlock.price)}</span> = <span className="font-bold">{formatCurrency(newBlock.count * newBlock.price)}</span>
                    </p>
                    <p className="text-xs md:text-sm text-[#666] mt-1">
                      Sur {newBlock.days} jours
                    </p>
                  </div>

                  <button
                    onClick={addSalesBlock}
                    className="mt-4 w-full bg-[#00FF94] text-black font-bold py-3 rounded-lg md:rounded-xl hover:bg-[#00FF94]/90 transition-colors flex items-center justify-center gap-2 text-sm md:text-base"
                  >
                    <Plus size={16} />
                    Générer les ventes
                  </button>
                </div>

                {/* Existing Blocks - Mobile optimized */}
                {salesBlocks.length > 0 && (
                  <div className="bg-[#111] border border-[#1a1a1a] rounded-xl md:rounded-2xl overflow-hidden">
                    <div className="px-4 md:px-6 py-3 md:py-4 border-b border-[#1a1a1a]">
                      <h3 className="text-sm md:text-base font-medium">Blocs générés</h3>
                    </div>
                    <div className="divide-y divide-[#1a1a1a]">
                      {salesBlocks.map((block) => (
                        <div key={block.id} className="px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm md:text-base font-medium">
                              {block.count} × {formatCurrency(block.price)}
                            </p>
                            <p className="text-xs md:text-sm text-[#666] truncate">
                              {formatCurrency(block.count * block.price)} • {block.days}j
                            </p>
                          </div>
                          <button
                            onClick={() => deleteBlock(block.id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors flex-shrink-0"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary - Mobile optimized */}
                <div className="bg-[#111] border border-[#1a1a1a] rounded-xl md:rounded-2xl p-4 md:p-6">
                  <h3 className="text-sm md:text-base font-medium mb-3 md:mb-4">Résumé</h3>
                  <div className="grid grid-cols-3 gap-2 md:gap-4">
                    <div className="bg-[#0a0a0a] rounded-lg md:rounded-xl p-3 md:p-4 border border-[#222]">
                      <p className="text-xs md:text-sm text-[#888]">Transactions</p>
                      <p className="text-lg md:text-2xl font-bold">{transactions.length}</p>
                    </div>
                    <div className="bg-[#0a0a0a] rounded-lg md:rounded-xl p-3 md:p-4 border border-[#222]">
                      <p className="text-xs md:text-sm text-[#888]">CA total</p>
                      <p className="text-lg md:text-2xl font-bold text-[#00FF94]">{formatCurrency(transactions.reduce((s, t) => s + t.amount, 0))}</p>
                    </div>
                    <div className="bg-[#0a0a0a] rounded-lg md:rounded-xl p-3 md:p-4 border border-[#222]">
                      <p className="text-xs md:text-sm text-[#888]">Blocs</p>
                      <p className="text-lg md:text-2xl font-bold">{salesBlocks.length}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* RDV TAB */}
        {tab === 'rdv' && (
          <div className="animate-fadeIn">
            {busy && <p className="text-center py-8 text-[#666]">Chargement...</p>}
            {!busy && <BookingsList items={bookings} />}
          </div>
        )}

        {/* STATS TAB */}
        {tab === 'stats' && (
          <div className="animate-fadeIn">
            {busy && <p className="text-center py-8 text-[#666]">Chargement...</p>}
            {!busy && stats && <StatsView data={stats} />}
          </div>
        )}

        {/* DISPO TAB */}
        {tab === 'dispo' && (
          <div className="animate-fadeIn">
            {busy && <p className="text-center py-8 text-[#666]">Chargement...</p>}
            {!busy && cfg && <ConfigView cfg={cfg} toggle={toggleD} save={saveCfg} />}
          </div>
        )}

        {/* PROSPECTS/CRM TAB */}
        {tab === 'prospects' && (
          <div className="animate-fadeIn">
            <ProspectsTab />
          </div>
        )}

        {/* EMAILS MARKETING TAB */}
        {tab === 'emails' && (
          <div className="animate-fadeIn">
            <EmailsTab />
          </div>
        )}
      </main>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

// Stats Card Component
function StatsCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-5 hover:border-[#333] transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#1a1a1a] flex items-center justify-center">
          <Icon size={18} className="text-[#00FF94]" />
        </div>
      </div>
      <p className="text-[#888] text-sm mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

// Bookings List Component
function BookingsList({ items }) {
  // Filter out past appointments (date AND time must have passed)
  const now = new Date();
  
  const upcomingBookings = items
    .filter(b => {
      // Parse booking date and time
      const [year, month, day] = b.booking_date.split('-').map(Number);
      const [hours, minutes] = b.booking_time.split(':').map(Number);
      const bookingDateTime = new Date(year, month - 1, day, hours, minutes);
      
      // Keep only if booking time hasn't passed yet
      return bookingDateTime > now;
    })
    .sort((a, b) => {
      // Sort by date and time (earliest first)
      const [yearA, monthA, dayA] = a.booking_date.split('-').map(Number);
      const [hoursA, minutesA] = a.booking_time.split(':').map(Number);
      const dateA = new Date(yearA, monthA - 1, dayA, hoursA, minutesA);
      
      const [yearB, monthB, dayB] = b.booking_date.split('-').map(Number);
      const [hoursB, minutesB] = b.booking_time.split(':').map(Number);
      const dateB = new Date(yearB, monthB - 1, dayB, hoursB, minutesB);
      
      return dateA - dateB;
    });

  // Check if booking is new (within last 24 hours)
  const isNew = (createdAt) => {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const hoursDiff = (now - created) / (1000 * 60 * 60);
    return hoursDiff < 24;
  };

  // Generate .ics file for Apple Calendar
  const generateICS = (booking) => {
    const [year, month, day] = booking.booking_date.split('-').map(Number);
    const [hours, minutes] = booking.booking_time.split(':').map(Number);
    const startDate = new Date(year, month - 1, day, hours, minutes);
    const endDate = new Date(startDate.getTime() + 30 * 60000); // 30 min meeting
    
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Hustler Digital Academy//RDV//FR
BEGIN:VEVENT
UID:${booking.id || Date.now()}@hustler-academy
DTSTAMP:${formatDate(now)}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:RDV - ${booking.first_name} ${booking.last_name}
DESCRIPTION:Client: ${booking.first_name} ${booking.last_name}\\nInstagram: @${booking.instagram}\\nEmail: ${booking.email}\\nTéléphone: ${booking.phone || 'Non renseigné'}\\nMotivation: ${booking.motivation_level}/10\\nBudget: ${booking.budget_range}
LOCATION:Instagram @${booking.instagram}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rdv-${booking.first_name}-${booking.last_name}.ics`;
    link.click();
  };

  if (!upcomingBookings.length) return <p className="text-center py-8 text-[#666]">Aucun rendez-vous à venir</p>;
  
  return (
    <div className="space-y-4">
      <p className="text-lg font-semibold">{upcomingBookings.length} rendez-vous à venir</p>
      {upcomingBookings.map((b, i) => (
        <div key={i} className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-5 hover:border-[#333] transition-colors relative">
          {/* Badge Nouveau */}
          {isNew(b.created_at) && (
            <div className="absolute -top-2 -right-2 bg-[#00FF94] text-black text-xs font-bold px-2 py-1 rounded-full animate-pulse">
              NOUVEAU
            </div>
          )}
          
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <p className="font-bold text-lg">{b.first_name} {b.last_name}</p>
              <p className="text-[#00FF94]">@{b.instagram}</p>
              <p className="text-[#666] text-sm">{b.email}</p>
              {b.phone && <p className="text-[#888] text-sm">📞 {b.phone}</p>}
            </div>
            <div className="text-right">
              <p className="text-[#888]">{b.booking_date}</p>
              <p className="text-[#00FF94] text-xl font-bold">{b.booking_time}</p>
              {/* Bouton Calendrier Apple */}
              <button 
                onClick={() => generateICS(b)}
                className="mt-2 text-xs bg-[#1a1a1a] hover:bg-[#333] text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 ml-auto"
              >
                <Calendar size={12} />
                Ajouter au calendrier
              </button>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#1a1a1a] grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <span><span className="text-[#666]">Âge:</span> {b.age_range}</span>
            <span><span className="text-[#666]">Lieu:</span> {b.continent}</span>
            <span><span className="text-[#666]">Budget:</span> {b.budget_range}</span>
            <span><span className="text-[#666]">Motiv:</span> {b.motivation_level}/10</span>
          </div>
          {b.open_answer && <p className="mt-3 pt-3 border-t border-[#1a1a1a] text-[#888] text-sm italic">"{b.open_answer}"</p>}
        </div>
      ))}
    </div>
  );
}

// Stats View Component - Simple and clear version
function StatsView({ data }) {
  return (
    <div className="space-y-4">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-[#888] text-xs mb-1">Visiteurs</p>
          <p className="text-2xl font-bold text-white">{data.total_views}</p>
        </div>
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-[#888] text-xs mb-1">Clics</p>
          <p className="text-2xl font-bold text-white">{data.total_clicks}</p>
        </div>
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-[#888] text-xs mb-1">Formulaires</p>
          <p className="text-2xl font-bold text-white">{data.form_starts}</p>
        </div>
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-[#888] text-xs mb-1">RDV pris</p>
          <p className="text-2xl font-bold text-white">{data.form_completes}</p>
        </div>
      </div>
      
      {/* Conversion */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-[#888] text-xs mb-1">Visiteurs (24h)</p>
          <p className="text-2xl font-bold text-white">{data.views_last_24h}</p>
        </div>
        <div className="bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-xl p-4">
          <p className="text-[#888] text-xs mb-1">Taux de conversion</p>
          <p className="text-2xl font-bold text-[#00FF94]">{data.conversion_rate}%</p>
        </div>
      </div>
      
      {/* Visiteurs par pays */}
      {data.countries && data.countries.length > 0 && (
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <h3 className="font-semibold mb-3 text-white">Visiteurs par pays</h3>
          <div className="space-y-2">
            {data.countries.map((c, i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-[#1a1a1a] last:border-0">
                <span className="text-white">{c.country || 'Inconnu'}</span>
                <span className="text-[#00FF94] font-bold">{c.count} visiteur{c.count > 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Sources */}
      {data.referrers && data.referrers.length > 0 && (
        <div className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-5">
          <h3 className="font-semibold mb-3">Sources</h3>
          {data.referrers.map((r, i) => (
            <div key={i} className="flex justify-between text-sm py-1">
              <span className="text-[#666] truncate mr-2">{r.referrer || 'Direct'}</span>
              <span className="text-[#00FF94]">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Config View Component
function ConfigView({ cfg, toggle, save }) {
  const days = [['monday','Lun'],['tuesday','Mar'],['wednesday','Mer'],['thursday','Jeu'],['friday','Ven'],['saturday','Sam'],['sunday','Dim']];
  return (
    <div className="space-y-4">
      <div className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-5">
        <h3 className="font-semibold mb-4">Jours disponibles</h3>
        <div className="flex flex-wrap gap-2">
          {days.map(([d, lbl]) => (
            <button key={d} onClick={() => toggle(d)} className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${cfg.days_available?.includes(d) ? 'bg-[#00FF94] text-black' : 'bg-[#1a1a1a] text-[#666] hover:bg-[#222]'}`}>{lbl}</button>
          ))}
        </div>
      </div>
      <button onClick={save} className="w-full bg-[#00FF94] text-black font-bold py-3 rounded-xl hover:bg-[#00FF94]/90 transition-colors">Sauvegarder</button>
    </div>
  );
}

// ==================== PROSPECTS/CRM TAB ====================
function ProspectsTab() {
  const [prospects, setProspects] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editingNotes, setEditingNotes] = useState(null);
  const [tempNotes, setTempNotes] = useState('');

  const statusLabels = {
    nouveau: { label: 'Nouveau', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    rdv_reserve: { label: 'RDV Réservé', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    rdv_passe: { label: 'RDV Passé', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    paye: { label: 'Payé', color: 'bg-[#00FF94]/20 text-[#00FF94] border-[#00FF94]/30' },
    a_relancer: { label: 'À Relancer', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    clos: { label: 'Clos', color: 'bg-[#666]/20 text-[#888] border-[#666]/30' }
  };

  useEffect(() => {
    fetchProspects();
  }, [filter, search]);

  const fetchProspects = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/prospects?`;
      if (filter !== 'all') url += `status=${filter}&`;
      if (search) url += `search=${encodeURIComponent(search)}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setProspects(data.prospects || []);
      setStatusCounts(data.status_counts || {});
    } catch (error) {
      console.error('Error fetching prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (prospectId, newStatus) => {
    try {
      await fetch(`${API}/api/prospects/${prospectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchProspects();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const saveNotes = async (prospectId) => {
    try {
      await fetch(`${API}/api/prospects/${prospectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: tempNotes })
      });
      setEditingNotes(null);
      fetchProspects();
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const updateClosing = async (prospectId, payload) => {
    try {
      await fetch(`${API}/api/prospects/${prospectId}/closing`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      fetchProspects();
    } catch (error) {
      console.error('Error updating closing:', error);
    }
  };

  const deleteProspect = async (prospectId) => {
    if (!window.confirm('Supprimer ce prospect ?')) return;
    try {
      await fetch(`${API}/api/prospects/${prospectId}`, { method: 'DELETE' });
      fetchProspects();
    } catch (error) {
      console.error('Error deleting prospect:', error);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
        {Object.entries(statusLabels).map(([key, { label, color }]) => (
          <button
            key={key}
            onClick={() => setFilter(filter === key ? 'all' : key)}
            className={`p-3 rounded-xl border transition-all ${filter === key ? color : 'bg-[#111] border-[#1a1a1a] hover:border-[#333]'}`}
          >
            <p className="text-lg md:text-2xl font-bold">{statusCounts[key] || 0}</p>
            <p className="text-xs text-[#888] truncate">{label}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
        <input
          type="text"
          placeholder="Rechercher un prospect..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#111] border border-[#1a1a1a] rounded-xl pl-12 pr-4 py-3 text-white focus:border-[#00FF94] focus:outline-none transition-colors"
        />
      </div>

      {/* Prospects List */}
      {loading ? (
        <p className="text-center py-8 text-[#666]">Chargement...</p>
      ) : prospects.length === 0 ? (
        <p className="text-center py-8 text-[#666]">Aucun prospect trouvé</p>
      ) : (
        <div className="space-y-3">
          {prospects.map((p) => (
            <div key={p.id} className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4 hover:border-[#333] transition-colors">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="font-bold text-white">{p.first_name} {p.last_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusLabels[p.status]?.color || statusLabels.nouveau.color}`}>
                      {statusLabels[p.status]?.label || 'Nouveau'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <p className="text-[#00FF94]">@{p.instagram}</p>
                    <p className="text-[#888]">{p.email}</p>
                    <p className="text-[#888]">{p.phone || '-'}</p>
                    <p className="text-[#888]">{p.booking_date} {p.booking_time}</p>
                  </div>
                  <div className="mt-2 text-xs text-[#666]">
                    Budget: {p.budget_range} • Motiv: {p.motivation_level}/10
                  </div>
                  {(p.utm_source || p.utm_medium || p.utm_campaign) && (
                    <div
                      data-testid={`prospect-utm-${p.id}`}
                      className="mt-2 text-xs flex flex-wrap gap-1.5"
                    >
                      {p.utm_source && (
                        <span className="px-2 py-0.5 rounded-md bg-[#0f2b1e] text-[#8FD4A8] border border-[#1e4a34]">
                          source: {p.utm_source}
                        </span>
                      )}
                      {p.utm_medium && (
                        <span className="px-2 py-0.5 rounded-md bg-[#0f2b1e] text-[#8FD4A8] border border-[#1e4a34]">
                          medium: {p.utm_medium}
                        </span>
                      )}
                      {p.utm_campaign && (
                        <span className="px-2 py-0.5 rounded-md bg-[#0f2b1e] text-[#8FD4A8] border border-[#1e4a34]">
                          campagne: {p.utm_campaign}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <select
                    value={p.status || 'nouveau'}
                    onChange={(e) => updateStatus(p.id, e.target.value)}
                    className="bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:border-[#00FF94] focus:outline-none"
                  >
                    {Object.entries(statusLabels).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => { setEditingNotes(p.id); setTempNotes(p.notes || ''); }}
                    className="p-2 bg-[#1a1a1a] hover:bg-[#333] rounded-lg transition-colors"
                    title="Notes"
                  >
                    <MessageSquare size={16} />
                  </button>
                  <button
                    onClick={() => deleteProspect(p.id)}
                    className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Notes Section */}
              {editingNotes === p.id && (
                <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
                  <textarea
                    value={tempNotes}
                    onChange={(e) => setTempNotes(e.target.value)}
                    placeholder="Notes sur ce prospect..."
                    className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-sm text-white focus:border-[#00FF94] focus:outline-none resize-none"
                    rows={3}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => saveNotes(p.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#00FF94] text-black text-sm font-medium rounded-lg"
                    >
                      <Save size={14} /> Sauvegarder
                    </button>
                    <button
                      onClick={() => setEditingNotes(null)}
                      className="px-3 py-1.5 text-[#888] text-sm hover:text-white"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
              {p.notes && editingNotes !== p.id && (
                <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
                  <p className="text-sm text-[#888] italic">📝 {p.notes}</p>
                </div>
              )}

              {/* Setter checklist (read-only) */}
              {p.checklist && (
                <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
                  <p className="text-xs text-[#8FD4A8] uppercase tracking-wider font-semibold mb-2">Checklist Setter</p>
                  <div className="grid grid-cols-2 gap-1.5 text-xs">
                    {[
                      ['called', 'Appelé'],
                      ['confirmed', 'RDV confirmé'],
                      ['video_sent', 'Vidéo envoyée'],
                      ['reminded', 'Relancé J-1'],
                    ].map(([key, label]) => {
                      const done = !!p.checklist?.[key]?.done;
                      return (
                        <div
                          key={key}
                          data-testid={`admin-checklist-${key}-${p.id}`}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded ${done ? 'bg-[#0f2b1e] text-[#8FD4A8]' : 'bg-[#0a0a0a] text-[#666]'}`}
                        >
                          <span>{done ? '✓' : '○'}</span>
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Setter notes_log (read-only) */}
              {(p.notes_log && p.notes_log.length > 0) && (
                <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
                  <p className="text-xs text-[#8FD4A8] uppercase tracking-wider font-semibold mb-2">Notes du setter</p>
                  <div className="space-y-1.5 max-h-40 overflow-auto">
                    {p.notes_log.slice().reverse().map((n, i) => (
                      <div key={i} className="bg-[#0a0a0a] rounded px-2 py-1.5 text-xs">
                        <p className="text-[#ccc]">{n.text}</p>
                        <p className="text-[#555] mt-0.5">{new Date(n.date).toLocaleString('fr-FR')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Closing (admin only) */}
              <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
                <p className="text-xs text-[#8FD4A8] uppercase tracking-wider font-semibold mb-2">Closing</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={p.closing_status || 'en_attente'}
                    onChange={(e) => updateClosing(p.id, { closing_status: e.target.value })}
                    data-testid={`closing-select-${p.id}`}
                    className="bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:border-[#00FF94] focus:outline-none"
                  >
                    <option value="en_attente">En attente</option>
                    <option value="present">Présent</option>
                    <option value="absent">Absent</option>
                    <option value="close">Closé</option>
                    <option value="pas_close">Pas closé</option>
                  </select>
                  {p.closing_status === 'close' && (
                    <input
                      type="number"
                      inputMode="decimal"
                      placeholder="Montant €"
                      defaultValue={p.closing_amount || ''}
                      data-testid={`closing-amount-${p.id}`}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val === '') return;
                        updateClosing(p.id, { closing_amount: parseFloat(val) });
                      }}
                      className="bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm w-28 focus:border-[#00FF94] focus:outline-none"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== EMAILS MARKETING TAB ====================
function EmailsTab() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchEmails();
    fetchTemplates();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/emails`);
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/email-templates`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const filteredEmails = emails.filter(e => 
    e.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (emailId) => {
    setSelected(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const selectAll = () => {
    if (selected.length === filteredEmails.length) {
      setSelected([]);
    } else {
      setSelected(filteredEmails.map(e => e.id));
    }
  };

  const deleteEmail = async (emailId) => {
    try {
      await fetch(`${API}/api/emails/${emailId}`, { method: 'DELETE' });
      fetchEmails();
      setSelected(prev => prev.filter(id => id !== emailId));
    } catch (error) {
      console.error('Error deleting email:', error);
    }
  };

  const editFirstName = async (e) => {
    const current = e.first_name || '';
    const name = window.prompt(`Prénom pour ${e.email} (laisser vide pour auto-déduire) :`, current);
    if (name === null) return;
    try {
      await fetch(`${API}/api/emails/${e.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: name }),
      });
      fetchEmails();
    } catch (err) {
      alert("Erreur lors de la mise à jour du prénom");
    }
  };

  const insertVariable = (variable) => {
    setEmailContent(prev => `${prev}${variable}`);
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      alert('Remplis le sujet et le contenu du mail');
      return;
    }
    
    const toEmails = selected.length > 0 
      ? emails.filter(e => selected.includes(e.id)).map(e => e.email)
      : filteredEmails.map(e => e.email);
    
    if (toEmails.length === 0) {
      alert('Sélectionne au moins un destinataire');
      return;
    }
    
    if (!window.confirm(`Envoyer à ${toEmails.length} destinataire(s) ?`)) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_emails: toEmails,
          subject: emailSubject,
          content: emailContent
        })
      });
      const data = await res.json();
      alert(data.message);
      setShowComposer(false);
      setEmailSubject('');
      setEmailContent('');
      setSelected([]);
    } catch (error) {
      alert('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async () => {
    const name = prompt('Nom du template:');
    if (!name) return;
    
    try {
      await fetch(`${API}/api/email-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: Date.now().toString(),
          name,
          subject: emailSubject,
          content: emailContent,
          created_at: new Date().toISOString()
        })
      });
      fetchTemplates();
      alert('Template sauvegardé !');
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const loadTemplate = (template) => {
    setEmailSubject(template.subject);
    setEmailContent(template.content);
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await fetch(`${API}/api/email-templates/${templateId}`, { method: 'DELETE' });
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Info banner about email configuration */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
        <p className="text-emerald-400 text-sm font-medium mb-1">✅ Brevo connecté</p>
        <p className="text-emerald-300/70 text-xs">
          Les campagnes sont envoyées via <a href="https://app.brevo.com" target="_blank" rel="noopener noreferrer" className="underline">Brevo</a> depuis <span className="font-mono">HDA &lt;sambakabigsam@gmail.com&gt;</span>. Pour utiliser un domaine personnalisé, ajoute-le et valide-le dans Brevo → Sender, Domain &amp; IP.
        </p>
      </div>

      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-2xl md:text-3xl font-bold text-[#00FF94]">{emails.length}</p>
          <p className="text-xs text-[#888]">Emails collectés</p>
        </div>
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-2xl md:text-3xl font-bold">{selected.length}</p>
          <p className="text-xs text-[#888]">Sélectionnés</p>
        </div>
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl p-4">
          <p className="text-2xl md:text-3xl font-bold">{templates.length}</p>
          <p className="text-xs text-[#888]">Templates</p>
        </div>
        <button
          onClick={() => setShowComposer(!showComposer)}
          className="bg-[#00FF94] text-black rounded-xl p-4 font-bold flex items-center justify-center gap-2 hover:bg-[#00FF94]/90 transition-colors"
        >
          <Send size={20} />
          <span className="hidden md:inline">Nouvelle campagne</span>
          <span className="md:hidden">Envoyer</span>
        </button>
      </div>

      {/* Email Composer */}
      {showComposer && (
        <div className="bg-[#111] border border-[#00FF94]/30 rounded-xl p-4 md:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">Composer un email</h3>
            <button onClick={() => setShowComposer(false)} className="text-[#888] hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Templates */}
          {templates.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-[#888]">Templates:</span>
              {templates.map(t => (
                <div key={t.id} className="flex items-center gap-1">
                  <button
                    onClick={() => loadTemplate(t)}
                    className="text-xs bg-[#1a1a1a] hover:bg-[#333] px-2 py-1 rounded transition-colors"
                  >
                    {t.name}
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            type="text"
            placeholder="Sujet de l'email (ex: Salut {{prenom}}, j'ai une question...)"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00FF94] focus:outline-none"
          />

          <textarea
            placeholder="Contenu de l'email (HTML supporté). Ex: <p>Salut {{prenom}} !</p>"
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00FF94] focus:outline-none resize-none font-mono text-sm"
            rows={8}
          />

          {/* Variables helper */}
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-3">
            <p className="text-xs text-[#888] mb-2">
              💡 Variables disponibles (chaque email sera personnalisé automatiquement) :
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => insertVariable('{{prenom}}')}
                className="text-xs bg-[#00FF94]/10 hover:bg-[#00FF94]/20 border border-[#00FF94]/30 text-[#00FF94] px-3 py-1.5 rounded-lg font-mono transition-colors"
                data-testid="insert-prenom-variable-btn"
              >
                {`{{prenom}}`} → Samba / Noah...
              </button>
              <button
                type="button"
                onClick={() => insertVariable('{{email}}')}
                className="text-xs bg-[#1a1a1a] hover:bg-[#222] border border-[#333] text-[#ccc] px-3 py-1.5 rounded-lg font-mono transition-colors"
                data-testid="insert-email-variable-btn"
              >
                {`{{email}}`} → son adresse
              </button>
            </div>
            <p className="text-[10px] text-[#666] mt-2">
              Le prénom est auto-déduit depuis les RDV existants ou l'adresse email. Clique sur un email dans la liste pour le modifier manuellement.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] hover:bg-[#333] rounded-lg transition-colors"
            >
              <Eye size={16} />
              Prévisualiser
            </button>
            <button
              onClick={saveTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] hover:bg-[#333] rounded-lg transition-colors"
            >
              <Save size={16} />
              Sauvegarder template
            </button>
            <button
              onClick={sendEmail}
              disabled={sending}
              className="flex items-center gap-2 px-6 py-2 bg-[#00FF94] text-black font-bold rounded-lg hover:bg-[#00FF94]/90 transition-colors disabled:opacity-50"
            >
              <Send size={16} />
              {sending ? 'Envoi...' : `Envoyer (${selected.length || filteredEmails.length})`}
            </button>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="bg-white text-black rounded-lg p-4 max-h-64 overflow-auto">
              <p className="font-bold mb-2">Sujet: {emailSubject}</p>
              <hr className="mb-2" />
              <div dangerouslySetInnerHTML={{ __html: emailContent }} />
            </div>
          )}
        </div>
      )}

      {/* Search + Select All */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
          <input
            type="text"
            placeholder="Rechercher un email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#111] border border-[#1a1a1a] rounded-xl pl-12 pr-4 py-3 text-white focus:border-[#00FF94] focus:outline-none transition-colors"
          />
        </div>
        <button
          onClick={selectAll}
          className="px-4 py-3 bg-[#111] border border-[#1a1a1a] hover:border-[#333] rounded-xl transition-colors flex items-center gap-2"
        >
          <Check size={16} className={selected.length === filteredEmails.length && filteredEmails.length > 0 ? 'text-[#00FF94]' : ''} />
          {selected.length === filteredEmails.length && filteredEmails.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
      </div>

      {/* Emails List */}
      {loading ? (
        <p className="text-center py-8 text-[#666]">Chargement...</p>
      ) : filteredEmails.length === 0 ? (
        <p className="text-center py-8 text-[#666]">Aucun email collecté</p>
      ) : (
        <div className="bg-[#111] border border-[#1a1a1a] rounded-xl overflow-hidden">
          <div className="divide-y divide-[#1a1a1a]">
            {filteredEmails.map((e) => (
              <div
                key={e.id}
                className={`flex items-center justify-between px-4 py-3 hover:bg-[#1a1a1a]/50 transition-colors cursor-pointer ${selected.includes(e.id) ? 'bg-[#00FF94]/10' : ''}`}
                onClick={() => toggleSelect(e.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${selected.includes(e.id) ? 'bg-[#00FF94] border-[#00FF94]' : 'border-[#444]'}`}>
                    {selected.includes(e.id) && <Check size={12} className="text-black" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white truncate">{e.email}</p>
                      {e.first_name ? (
                        <span className="text-[10px] uppercase tracking-wide bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/30 px-2 py-0.5 rounded">
                          {e.first_name}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wide bg-[#1a1a1a] text-[#666] border border-[#333] px-2 py-0.5 rounded">
                          prénom auto
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#666]">{new Date(e.created_at).toLocaleDateString('fr-FR')} • {e.source}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(ev) => { ev.stopPropagation(); editFirstName(e); }}
                    className="px-2 py-1 text-xs text-[#888] hover:text-[#00FF94] hover:bg-[#00FF94]/10 rounded-lg transition-colors"
                    data-testid={`edit-prenom-${e.id}`}
                  >
                    Prénom
                  </button>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); deleteEmail(e.id); }}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

// ==================== CASE STUDY VIDEO CONFIG ====================
function CaseStudyVideoConfig() {
  const [url, setUrl] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/settings/case-study-video`)
      .then((r) => r.json())
      .then((d) => setUrl(d.value || ''))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    await fetch(`${API}/api/settings/case-study-video`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: url.trim() }),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  if (loading) return null;

  return (
    <div className="bg-[#111] border border-[#1a1a1a] rounded-xl md:rounded-2xl p-4 md:p-6" data-testid="admin-case-study-config">
      <h3 className="text-sm md:text-base font-medium mb-1">Vidéo étude de cas (setter)</h3>
      <p className="text-xs text-[#888] mb-3">
        Lien copié par le setter en un tap depuis l&rsquo;espace /setters.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://youtu.be/…"
          data-testid="admin-case-study-input"
          className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2.5 text-sm focus:border-[#00FF94] focus:outline-none"
        />
        <button
          type="button"
          onClick={save}
          data-testid="admin-case-study-save"
          className="px-4 py-2.5 rounded-lg bg-[#00FF94] text-black text-sm font-semibold"
        >
          {saved ? 'Enregistré' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}
